/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  BUTTON_FORMAT_SOFT,
  BUTTON_THEME_DANGER,
  BUTTON_THEME_DEFAULT,
  Button,
  ErrorMessage,
  TOAST_TYPE_SUCCESS,
  Toast,
  ToastProps,
} from '@squiz/sds';
import { EntityDxpUserMeOrganisation } from 'apps/console-core/src/Services/console';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import { useConsoleServiceStore, useToastStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { lastValueFrom } from 'rxjs';
import { AjaxError } from 'rxjs/ajax';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import {
  EventBusDeleteScheduleDto,
  EventBusDeleteSubscriptionRuleDto,
  getEventBusService,
} from '../../../../Services/event-bus';
import { getEventSubscriptionPath } from '../../../../Utilities/GetEventSubscriptionPath/GetEventSubscriptionPath';
import { EVENT_SUBSCRIPTION_DELETE_DATA_IDS } from './EventSubscriptionDeleteForm.constants';
import { EventSubscriptionDeleteFormProps } from './EventSubscriptionDeleteForm.props';

export const EventSubscriptionDeleteForm = (props: EventSubscriptionDeleteFormProps): JSX.Element => {
  const { t } = useTranslation();

  const { setButtonKeys, setButtons, close, subscriptionId, subscriptionName } = props;

  const { tenant }: { tenant: EntityDxpUserMeOrganisation } = useConsoleServiceStore();
  const { updateActiveToast } = useToastStore();
  const eventBusService = getEventBusService();

  const [isFormSubmitted, setSubmit] = useState(false);
  const [error, setError] = useState(false);

  // Handle navigation
  const navigate = useNavigate();
  const eventSubscriptionsPath = useMemo(() => {
    return getCapabilityPath(`/event-service${getEventSubscriptionPath()}`);
  }, []);
  const returnToEventSubscriptionsList = useCallback((): void => {
    navigate(eventSubscriptionsPath);
  }, [eventSubscriptionsPath, navigate]);

  // Labels
  const labels = useMemo(() => {
    return {
      cancel: t(CONFIGURATION_STRINGS.cancel),
      delete: t(CONFIGURATION_STRINGS.delete),
      deleteConfirmationText: t(CONFIGURATION_STRINGS.deleteEventSubscriptionDialogContent),
      deleteErrorMessage: t(CONFIGURATION_STRINGS.deleteEventSubscriptionDialogError),
      toastFailContent1: t(CONFIGURATION_STRINGS.eventSubscriptionDeletedFailToast1),
      toastFailContent2: t(CONFIGURATION_STRINGS.eventSubscriptionDeletedFailToast2),
      toastFailTitle: t(CONFIGURATION_STRINGS.error),
      toastSuccessContent1: t(CONFIGURATION_STRINGS.eventSubscriptionDeletedSuccessToast1),
      toastSuccessContent2: t(CONFIGURATION_STRINGS.eventSubscriptionDeletedSuccessToast2),
      toastSuccessTitle: t(CONFIGURATION_STRINGS.eventSubscriptionDeleted),
    };
  }, [t]);

  // Prepare toast props
  const successToastProps: ToastProps = useMemo(() => {
    return {
      showToast: true,
      stateHandler: useToastStore,
      title: labels.toastSuccessTitle,
      toastContent: (
        <span>
          {labels.toastSuccessContent1}
          <i>{subscriptionName}</i>
          {labels.toastSuccessContent2}
        </span>
      ),
      toastType: TOAST_TYPE_SUCCESS,
    };
  }, [labels, subscriptionName]);

  const deleteRule = useCallback(async (): Promise<void> => {
    const dto: EventBusDeleteSubscriptionRuleDto = {
      subscriptionId,
      tenantId: tenant.tenantReferenceId,
    };

    await lastValueFrom(eventBusService.deleteEventSubscriptionRule(dto));
  }, [eventBusService, subscriptionId, tenant]);

  const deleteSchedule = useCallback(async (): Promise<void> => {
    const dto: EventBusDeleteScheduleDto = {
      scheduleName: subscriptionId,
    };

    await lastValueFrom(eventBusService.deleteEventSchedule(dto));
  }, [eventBusService, subscriptionId]);

  const submitHandler = useCallback(async (): Promise<void> => {
    setError(false);

    try {
      await deleteRule();

      try {
        await deleteSchedule();
      } catch (err) {
        // no-op if schedule already does not exist
        if ((err as AjaxError).status !== 404) {
          throw err;
        }
      }

      updateActiveToast(null);
      updateActiveToast(<Toast {...successToastProps} />);

      close();
      returnToEventSubscriptionsList();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);

      setError(true);
      setSubmit(false);
    }
  }, [
    close,
    deleteRule,
    deleteSchedule,
    returnToEventSubscriptionsList,
    setError,
    successToastProps,
    updateActiveToast,
  ]);

  useEffect(() => {
    const cancelButton = (
      <Button
        data-id={EVENT_SUBSCRIPTION_DELETE_DATA_IDS.cancelButton}
        format={BUTTON_FORMAT_SOFT}
        onClick={(): void => close()}
        text={labels.cancel}
        theme={BUTTON_THEME_DEFAULT}
      />
    );
    const deleteButton = (
      <Button
        data-id={EVENT_SUBSCRIPTION_DELETE_DATA_IDS.deleteButton}
        isLoading={isFormSubmitted}
        onClick={(): void => {
          setSubmit(true);
          submitHandler();
        }}
        text={labels.delete}
        theme={BUTTON_THEME_DANGER}
      />
    );

    const buttonList = [];
    const buttonKeys = [];

    if (isFormSubmitted) {
      buttonKeys.push(`delete`);
      buttonList.push(deleteButton);
    } else {
      buttonKeys.push(`cancel`, `delete`);
      buttonList.push(cancelButton, deleteButton);
    }

    setButtonKeys(buttonKeys);
    setButtons(buttonList);
  }, [close, isFormSubmitted, labels, setButtonKeys, setButtons, submitHandler]);

  return (
    <>
      {labels.deleteConfirmationText}
      {error && (
        <div className="sds-form-error" data-id={EVENT_SUBSCRIPTION_DELETE_DATA_IDS.deleteErrorMessage}>
          <ErrorMessage message={labels.deleteErrorMessage} />
        </div>
      )}
    </>
  );
};
